import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import {
  Box,
  Button,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import publicationsData from "../data/publications.json";
import {
  PublicationMetadata,
  publicationMetadata,
} from "../data/publicationMetadata";
import { researchDashboards } from "../data/research";

type PublicationAuthor = {
  first_name: string;
  last_name: string;
};

type Publication = {
  title: string;
  url: string;
  authors: PublicationAuthor[];
  venue: string;
  keywords: string;
  logline: string;
  abstract: string;
};

const publications = publicationsData as Publication[];

const summarizeAuthors = (authors: PublicationAuthor[]) => {
  if (authors.length <= 4) {
    return authors
      .map((author) => `${author.first_name.charAt(0)}. ${author.last_name}`)
      .join(", ");
  }

  const firstAuthors = authors
    .slice(0, 3)
    .map((author) => `${author.first_name.charAt(0)}. ${author.last_name}`)
    .join(", ");

  return `${firstAuthors}, and ${authors.length - 3} others`;
};

const fallbackMetadata = (publication: Publication): PublicationMetadata => ({
  year: publication.venue.match(/\b(20\d{2})\b/)?.[1] || "2024",
  program: publication.keywords.split(",")[0] || "Research",
  logline:
    publication.logline ||
    publication.abstract.split(". ").slice(0, 2).join(". ").trim() ||
    publication.venue,
  chips: publication.keywords
    ? publication.keywords
        .split(",")
        .map((keyword) => keyword.trim())
        .slice(0, 3)
    : [publication.venue],
  actions: [
    {
      label: "Open",
      href: publication.url,
      external: !publication.url.startsWith("/"),
    },
  ],
});

const isExternal = (href: string, external?: boolean) =>
  external || href.startsWith("http");

const PublicationActions = ({
  actions,
}: {
  actions: PublicationMetadata["actions"];
}) => (
  <Stack
    sx={{
      alignItems: { lg: "stretch", xs: "flex-start" },
      flexDirection: { lg: "column", xs: "row" },
      flexWrap: { lg: "nowrap", xs: "wrap" },
      gap: "10px",
      minWidth: { lg: "160px", xs: "auto" },
    }}
  >
    {actions.map((action) => {
      const external = isExternal(action.href, action.external);
      const primaryAction = action.label === "Paper" || action.label === "Open";

      return (
        <Button
          href={action.href}
          key={action.label}
          target={external ? "_blank" : undefined}
          rel={external ? "noreferrer" : undefined}
          size="small"
          variant={primaryAction ? "contained" : "outlined"}
          endIcon={<ArrowOutwardIcon sx={{ fontSize: "16px" }} />}
          sx={(theme) => ({
            backgroundColor:
              primaryAction
                ? theme.customColors.primary.prusianBlue
                : "transparent",
            borderColor: theme.customColors.primary.prusianBlue,
            borderRadius: "8px",
            color:
              primaryAction
                ? theme.customColors.primary.aliceBlue
                : theme.customColors.primary.prusianBlue,
            fontSize: "14px",
            fontWeight: 800,
            justifyContent: "space-between",
            minWidth: { lg: "160px", xs: "auto" },
            padding: "7px 12px",
            ":hover": {
              backgroundColor: theme.customColors.primary.lion,
              borderColor: theme.customColors.primary.lion,
              color: "#fff",
            },
          })}
        >
          {action.label}
        </Button>
      );
    })}
  </Stack>
);

const Publications = () => {
  const theme = useTheme();
  const enrichedPublications = publications.map((publication) => ({
    publication,
    metadata:
      publicationMetadata[publication.title] || fallbackMetadata(publication),
  }));
  const years = Array.from(
    new Set(enrichedPublications.map(({ metadata }) => metadata.year)),
  ).sort((a, b) => b.localeCompare(a));

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "auto",
        padding: { lg: "80px 0 125px", sm: "80px 0 100px", xs: "50px 0 80px" },
      }}
    >
      <Grid container spacing={{ lg: 5, xs: 3 }}>
        <Grid item lg={9} xs={12}>
          <Typography
            sx={{
              fontSize: { lg: "96px", md: "80px", sm: "60px", xs: "48px" },
              lineHeight: "100%",
              fontWeight: 650,
              color: theme.customColors.primary.prusianBlue,
              marginBottom: { lg: "24px", xs: "18px" },
            }}
            variant="h1"
          >
            Publications
          </Typography>
          <Typography
            sx={{
              color: theme.customColors.primary.prusianBlue,
              fontSize: { lg: "28px", md: "24px", xs: "19px" },
              fontWeight: 500,
              lineHeight: 1.35,
              maxWidth: "900px",
            }}
          >
            Research papers, policy outputs, and companion infrastructure from
            the Data Provenance Initiative.
          </Typography>
        </Grid>
      </Grid>

      <Box sx={{ marginTop: { lg: "52px", xs: "34px" } }}>
        {years.map((year) => (
          <Box key={year} sx={{ marginBottom: { lg: "54px", xs: "40px" } }}>
            <Typography
              sx={{
                color: theme.customColors.supplementary.horizon,
                fontSize: { lg: "30px", xs: "24px" },
                fontWeight: 800,
                lineHeight: 1,
                marginBottom: "14px",
              }}
            >
              {year}
            </Typography>
            <Box
              sx={{
                borderTop: `1px solid ${theme.customColors.primary.prusianBlue}`,
              }}
            >
              {enrichedPublications
                .filter(({ metadata }) => metadata.year === year)
                .map(({ publication, metadata }) => (
                  <Box
                    key={publication.title}
                    sx={{
                      borderBottom: `1px solid ${theme.customColors.primary.prusianBlue}`,
                      display: "grid",
                      gridTemplateColumns: {
                        lg: "210px 1fr 260px",
                        md: "180px 1fr",
                        xs: "1fr",
                      },
                      gap: { lg: "28px", md: "22px", xs: "14px" },
                      padding: { lg: "24px 0", xs: "20px 0" },
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: theme.customColors.primary.lion,
                          fontSize: "13px",
                          fontWeight: 800,
                          lineHeight: 1.2,
                          marginBottom: "8px",
                          textTransform: "uppercase",
                        }}
                      >
                        {metadata.program}
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.customColors.supplementary.horizon,
                          fontSize: "15px",
                          fontWeight: 700,
                          lineHeight: 1.3,
                        }}
                      >
                        {publication.venue}
                      </Typography>
                    </Box>
                    <Box>
                      <MuiLink
                        href={publication.url}
                        target={
                          isExternal(publication.url) ? "_blank" : undefined
                        }
                        rel={
                          isExternal(publication.url) ? "noreferrer" : undefined
                        }
                        underline="none"
                      >
                        <Typography
                          sx={{
                            color: theme.customColors.primary.prusianBlue,
                            fontSize: { lg: "30px", md: "26px", xs: "22px" },
                            fontWeight: 800,
                            lineHeight: 1.12,
                            marginBottom: "10px",
                            ":hover": {
                              color: theme.customColors.primary.lion,
                            },
                          }}
                        >
                          {publication.title}
                        </Typography>
                      </MuiLink>
                      <Typography
                        sx={{
                          color: theme.customColors.primary.prusianBlue,
                          fontSize: { lg: "18px", xs: "16px" },
                          lineHeight: 1.45,
                          maxWidth: "880px",
                          marginBottom: "14px",
                        }}
                      >
                        {metadata.logline}
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.customColors.supplementary.horizon,
                          fontSize: "14px",
                          lineHeight: 1.35,
                        }}
                      >
                        {summarizeAuthors(publication.authors)}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          flexWrap: "wrap",
                          gap: "8px",
                          marginTop: "16px",
                          maxWidth: "760px",
                        }}
                      >
                        {metadata.chips.slice(0, 3).map((chip) => (
                          <Typography
                            key={chip}
                            sx={{
                              backgroundColor: "rgba(161, 221, 255, 0.18)",
                              border: `1px solid ${theme.customColors.supplementary.horizon}`,
                              borderRadius: "999px",
                              color: theme.customColors.supplementary.horizon,
                              display: "inline-flex",
                              fontSize: "12px",
                              fontWeight: 800,
                              lineHeight: 1,
                              padding: "6px 10px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {chip}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                    <Box
                      sx={{
                        alignSelf: { lg: "center", xs: "start" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: { lg: "flex-end", xs: "flex-start" },
                        }}
                      >
                        <PublicationActions actions={metadata.actions} />
                      </Box>
                    </Box>
                  </Box>
                ))}
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${theme.customColors.primary.prusianBlue}`,
          color: theme.customColors.primary.prusianBlue,
          marginTop: { lg: "72px", xs: "48px" },
          paddingTop: { lg: "34px", xs: "24px" },
        }}
      >
        <Typography
          sx={{
            color: theme.customColors.primary.lion,
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            marginBottom: "12px",
          }}
        >
          Data and dashboards
        </Typography>
        <Typography
          sx={{
            color: theme.customColors.primary.prusianBlue,
            fontSize: { lg: "34px", xs: "26px" },
            fontWeight: 800,
            lineHeight: 1.08,
            marginBottom: { lg: "24px", xs: "18px" },
            maxWidth: "860px",
          }}
          variant="h2"
        >
          Explore the measurement systems behind the papers.
        </Typography>
        <Grid container spacing={3}>
          {researchDashboards.map((dashboard) => (
            <Grid item lg={3} md={6} xs={12} key={dashboard.title}>
              <Box
                sx={{
                  border: `1px solid ${theme.customColors.primary.prusianBlue}`,
                  borderRadius: "8px",
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{ backgroundColor: dashboard.accent, height: "7px" }}
                />
                <Box sx={{ padding: { lg: "22px", xs: "20px" } }}>
                  <Typography
                    sx={{
                      color: theme.customColors.primary.lion,
                      fontSize: "12px",
                      fontWeight: 800,
                      letterSpacing: "0.08em",
                      marginBottom: "14px",
                      textTransform: "uppercase",
                    }}
                  >
                    {dashboard.label}
                  </Typography>
                  <Typography
                    sx={{
                      color: theme.customColors.primary.prusianBlue,
                      fontSize: { lg: "25px", xs: "23px" },
                      fontWeight: 850,
                      lineHeight: 1.05,
                      marginBottom: "8px",
                    }}
                  >
                    {dashboard.metric}
                  </Typography>
                  <Typography
                    sx={{
                      color: theme.customColors.supplementary.horizon,
                      fontSize: "14px",
                      fontWeight: 700,
                      lineHeight: 1.3,
                      marginBottom: "16px",
                    }}
                  >
                    {dashboard.detail}
                  </Typography>
                  <Typography
                    sx={{
                      color: theme.customColors.primary.prusianBlue,
                      fontSize: "15px",
                      lineHeight: 1.4,
                      marginBottom: "20px",
                    }}
                  >
                    {dashboard.description}
                  </Typography>
                  {dashboard.comingSoon ? (
                    <Button
                      disabled
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: "rgba(8, 31, 48, 0.35)",
                        borderRadius: "8px",
                        color: theme.customColors.supplementary.horizon,
                        fontSize: "14px",
                        fontWeight: 800,
                        padding: "7px 12px",
                        "&.Mui-disabled": {
                          borderColor: "rgba(8, 31, 48, 0.35)",
                          color: theme.customColors.supplementary.horizon,
                        },
                      }}
                    >
                      {dashboard.cta}
                    </Button>
                  ) : (
                    <Button
                      href={dashboard.href}
                      target={dashboard.external ? "_blank" : undefined}
                      rel={dashboard.external ? "noreferrer" : undefined}
                      size="small"
                      variant="outlined"
                      endIcon={<ArrowOutwardIcon sx={{ fontSize: "16px" }} />}
                      sx={{
                        borderColor: theme.customColors.primary.prusianBlue,
                        borderRadius: "8px",
                        color: theme.customColors.primary.prusianBlue,
                        fontSize: "14px",
                        fontWeight: 800,
                        padding: "7px 12px",
                        ":hover": {
                          backgroundColor: theme.customColors.primary.lion,
                          borderColor: theme.customColors.primary.lion,
                          color: "#fff",
                        },
                      }}
                    >
                      {dashboard.cta}
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default Publications;
